import { useMemo, useState } from 'react'

import type { AgriculturalMaterial } from '@/lib/api/agricultural-materials-api'
import type { AgriMaterialLot } from '@/lib/api/agri-material-lot-api'
import { MaterialThumb } from '@/components/agricultural-materials/material-thumb'
import { agriLotOption } from '@/components/lots/lot-option-builders'
import { Combobox } from '@/components/ui/combobox'
import { useAgriculturalMaterialsInfinite } from '@/hooks/use-agricultural-materials-query'
import { useAgriLotsInfinite } from '@/hooks/use-agri-material-lot-query'
import { useDebounced } from '@/hooks/use-debounced'
import { m } from '@/lib/paraglide/messages'

interface MaterialPickerProps {
    value?: string
    /** Tên vật tư để hiển thị khi value không nằm trong trang đã tải (màn sửa). */
    fallbackLabel?: string
    placeholder?: string
    onChange: (material: AgriculturalMaterial | undefined) => void
}

/** Chọn vật tư nông nghiệp — search API debounce + load-more khi cuộn
 *  (rule resource-select input, AGENTS.md). */
export function AgriMaterialPicker({
    value,
    fallbackLabel,
    placeholder,
    onChange,
}: MaterialPickerProps) {
    const [search, setSearch] = useState('')
    const debounced = useDebounced(search, 300)
    const query = useAgriculturalMaterialsInfinite(
        debounced ? { search: debounced } : {},
    )
    // Chỉ vật tư CÓ theo dõi tồn mới nhập/xuất kho được: vật tư bỏ tick "theo
    // dõi tồn" chỉ dùng để ghi nhật ký chăm sóc, không có tồn để nhập/xuất.
    // Backend cũng chặn (ErrMaterialNotStockTracked) — đây là lớp UI cho khỏi
    // chọn nhầm rồi mới báo lỗi.
    const materials = useMemo(
        () =>
            (query.data?.pages.flatMap((p) => p.data ?? []) ?? []).filter(
                (mm) => mm.is_active && !mm.is_external,
            ),
        [query.data],
    )
    const byId = useMemo(
        () => new Map(materials.map((mm) => [mm.id, mm])),
        [materials],
    )
    const options = materials.map((mm) => ({
        value: mm.id,
        label: mm.name,
        // Rich dropdown row with the material thumbnail; the flat label is still
        // used by Combobox for the selected-value display.
        render: () => (
            <span className="flex min-w-0 items-center gap-2">
                <MaterialThumb src={mm.image_url ?? undefined} />
                <span className="truncate">{mm.name}</span>
            </span>
        ),
    }))

    return (
        <Combobox
            options={options}
            value={value}
            onChange={(v) => onChange(byId.get(v))}
            onSearchChange={setSearch}
            shouldFilter={false}
            placeholder={placeholder ?? m.aml_form_material()}
            onLoadMore={() => query.fetchNextPage()}
            hasMore={query.hasNextPage}
            loadingMore={query.isFetchingNextPage}
            onOpenChange={(open) => {
                if (!open) setSearch('')
            }}
            selectedOption={
                value && fallbackLabel
                    ? { value, label: fallbackLabel }
                    : undefined
            }
        />
    )
}

interface LotPickerProps {
    /** Lọc theo vật tư; bỏ trống = picker lô toàn cục (như kho bao bì). */
    materialId?: string
    /** true → gồm lô ảo 'STOCK' (qty-mode) — dùng cho picker xuất. */
    includeVirtual?: boolean
    value?: string
    fallbackLabel?: string
    // Trả kèm lô đã chọn (khi có trong trang đã tải) để caller đọc material_unit
    // hiển thị đơn vị ở ô số lượng. undefined khi chọn từ nhãn fallback lúc sửa.
    onChange: (lotId: string, lot?: AgriMaterialLot) => void
}

/** Chọn lô — search API debounce + load-more khi cuộn. Chỉ lô còn khả dụng. */
export function AgriLotPicker({
    materialId,
    includeVirtual,
    value,
    fallbackLabel,
    onChange,
}: LotPickerProps) {
    const [search, setSearch] = useState('')
    const debounced = useDebounced(search, 300)
    const query = useAgriLotsInfinite({
        only_available: '1',
        ...(materialId ? { material_id: materialId } : {}),
        ...(includeVirtual ? { include_virtual: '1' as const } : {}),
        ...(debounced ? { search: debounced } : {}),
    })
    // Ẩn lô của vật tư đã bỏ tick "theo dõi tồn": không nhập/xuất được nữa
    // (backend chặn), hiện ra chỉ khiến người dùng chọn rồi mới bị báo lỗi.
    // Field thiếu (API cũ) → coi như còn theo dõi để không giấu nhầm lô hợp lệ.
    const lots = (query.data?.pages.flatMap((p) => p.data ?? []) ?? []).filter(
        (l) => l.material_is_external !== true,
    )
    // Dùng builder chung: option 2–3 dòng + dòng "Tạo: …" (lô ảo STOCK không có).
    const options = lots.map(agriLotOption)

    return (
        <Combobox
            options={options}
            value={value}
            onChange={(v) =>
                onChange(
                    v,
                    lots.find((l) => l.id === v),
                )
            }
            onSearchChange={setSearch}
            shouldFilter={false}
            placeholder={m.aml_form_pick_lot()}
            onLoadMore={() => query.fetchNextPage()}
            hasMore={query.hasNextPage}
            loadingMore={query.isFetchingNextPage}
            onOpenChange={(open) => {
                if (!open) setSearch('')
            }}
            selectedOption={
                value && fallbackLabel
                    ? { value, label: fallbackLabel }
                    : undefined
            }
        />
    )
}
